import tokens from './tokens';

// Component-specific styling based on privacv design system - light mode only
const components = {
  // Form components
  input: {
    root: {
      backgroundColor: tokens.colors.neutral[50],
      border: `1px solid ${tokens.colors.borders.light}`,
      borderRadius: tokens.radius.base,
      height: tokens.spacing.input.height,
      transition: tokens.transitions.standard,
      marginBottom: tokens.spacing.input.marginBottom,
      
      '&:hover': {
        borderColor: tokens.colors.primary.main,
      },
      
      '&:focus-within': {
        borderColor: tokens.colors.primary.main,
        boxShadow: tokens.shadows.highlight,
      },
      
      '& .MuiInputBase-root': {
        height: tokens.spacing.input.height,
        fontSize: tokens.typography.fontSizes.sm,
        fontFamily: tokens.typography.fontFamily.primary,
      },
      
      '& .MuiOutlinedInput-input': {
        padding: tokens.spacing.input.padding,
        fontSize: tokens.typography.fontSizes.sm,
      },
      
      '& .MuiInputLabel-root': {
        marginLeft: tokens.spacing.input.labelPadding.left,
        color: tokens.colors.text.secondary,
        fontSize: tokens.typography.fontSizes.sm,
        fontWeight: tokens.typography.fontWeights.normal,
        lineHeight: tokens.typography.lineHeights.tight,
        '&.Mui-focused, &.MuiFormLabel-filled': {
          color: tokens.colors.primary.main,
          marginLeft: 0,
        }
      },
      
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: tokens.colors.borders.light,
        borderRadius: tokens.radius.base,
      },
      
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: tokens.colors.primary.main,
      },
      
      '& .MuiInputBase-input': {
        color: tokens.colors.neutral[900],
      },
    },
    label: {
      fontSize: tokens.typography.fontSizes.sm,
      fontWeight: tokens.typography.fontWeights.medium,
      color: tokens.colors.text.secondary,
    },
  },

  // Cards
  card: {
    root: {
      backgroundColor: '#ffffff',
      borderRadius: tokens.radius.base,
      border: `1px solid ${tokens.colors.borders.light}`,
      boxShadow: tokens.shadows.soft,
      transition: tokens.transitions.standard,
      '&:hover': {
        boxShadow: tokens.shadows.elegant,
        transform: 'translateY(-2px)',
      },
      overflow: 'hidden',
    },
    header: {
      padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
      backgroundColor: 'transparent',
      borderBottom: `1px solid ${tokens.colors.borders.light}`,
    },
    content: {
      padding: `${tokens.spacing[4]} ${tokens.spacing[4]} ${tokens.spacing[4]}`,
      backgroundColor: 'transparent',
    },
  },

  // Buttons
  button: {
    base: {
      borderRadius: tokens.radius.base,
      fontSize: tokens.typography.fontSizes.sm,
      fontWeight: tokens.typography.fontWeights.medium,
      padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
      transition: tokens.transitions.smooth,
      boxShadow: 'none',
      fontFamily: tokens.typography.fontFamily.primary,
      textTransform: 'none',
      letterSpacing: tokens.typography.letterSpacing.wide,
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: tokens.shadows.sm,
      },
      '&:active': {
        transform: 'translateY(0)',
      },
    },
    primary: {
      backgroundColor: tokens.colors.primary.main,
      color: tokens.colors.primary.contrast,
      '&:hover': {
        backgroundColor: tokens.colors.primary.dark,
        boxShadow: tokens.shadows.highlight,
      },
    },
    secondary: {
      backgroundColor: tokens.colors.neutral[100],
      color: tokens.colors.primary.main,
      '&:hover': {
        backgroundColor: tokens.colors.neutral[200],
      },
    },
    outlined: {
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.colors.primary.main}`,
      color: tokens.colors.primary.main,
      '&:hover': {
        backgroundColor: 'rgba(52, 152, 219, 0.05)',
      },
    },
    text: {
      backgroundColor: 'transparent',
      color: tokens.colors.primary.main,
      '&:hover': {
        backgroundColor: 'rgba(52, 152, 219, 0.05)',
      },
    },
    destructive: {
      backgroundColor: tokens.colors.error,
      color: '#ffffff',
      '&:hover': {
        backgroundColor: '#e53e3e', // Darker red
      },
    },
  },

  // Container styles
  container: {
    root: {
      backgroundColor: tokens.colors.background.paper,
      borderRadius: tokens.radius.lg,
      padding: tokens.spacing[4],
      boxShadow: tokens.shadows.sm,
    },
  },

  // IconButton styles
  iconButton: {
    root: {
      color: tokens.colors.neutral[500],
      '&:hover': {
        color: tokens.colors.neutral[700],
        backgroundColor: tokens.colors.alpha.black[50],
      },
    },
  },

  // Rich text editor styles
  richText: {
    root: {
      '& .ql-toolbar': {
        border: `1px solid ${tokens.colors.borders.light}`,
        borderBottom: 'none',
        borderTopLeftRadius: tokens.radius.base,
        borderTopRightRadius: tokens.radius.base,
        backgroundColor: tokens.colors.neutral[50],
      },
      '& .ql-container': {
        border: `1px solid ${tokens.colors.borders.light}`,
        borderBottomLeftRadius: tokens.radius.base,
        borderBottomRightRadius: tokens.radius.base,
        backgroundColor: '#ffffff',
        minHeight: '150px',
      },
      '& .ql-editor': {
        color: tokens.colors.neutral[900],
        backgroundColor: 'transparent',
        fontFamily: tokens.typography.fontFamily.primary,
        fontSize: tokens.typography.fontSizes.sm,
      },
      '& .ql-editor.ql-blank::before': {
        color: tokens.colors.text.secondary,
        fontStyle: 'normal',
        fontFamily: tokens.typography.fontFamily.primary,
      },
      '& .ql-stroke': {
        stroke: '#000000',
      },
      '& .ql-fill': {
        fill: '#000000',
      },
      '& .ql-picker': {
        color: 'inherit',
      },
    },
  },

  // Add styles for multiline text fields
  textField: {
    root: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: 'transparent',
        borderRadius: tokens.radius.base,
        transition: tokens.transitions.standard,
        '& textarea': {
          color: tokens.colors.neutral[900],
          fontFamily: tokens.typography.fontFamily.primary,
        },
        '&:hover': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.colors.primary.main,
          }
        },
        '&.Mui-focused': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.colors.primary.main,
            borderWidth: '2px',
          },
          boxShadow: tokens.shadows.highlight,
        },
      },
    },
  },

  // Dialog styles
  dialog: {
    paper: {
      borderRadius: tokens.radius.xl,
      boxShadow: tokens.shadows.elegant,
      overflow: 'hidden',
    },
    title: {
      padding: `${tokens.spacing[4]} ${tokens.spacing[4]} ${tokens.spacing[2]}`,
      fontSize: tokens.typography.fontSizes.xl,
      fontWeight: tokens.typography.fontWeights.semibold,
    },
    content: {
      padding: `${tokens.spacing[2]} ${tokens.spacing[4]} ${tokens.spacing[4]}`,
    },
    actions: {
      padding: `${tokens.spacing[2]} ${tokens.spacing[4]} ${tokens.spacing[4]}`,
    },
  },

  // App bar / Header styles
  appBar: {
    root: {
      boxShadow: tokens.shadows.sm,
      backgroundColor: tokens.colors.alpha.black[800],
      backdropFilter: 'blur(8px)',
      transition: tokens.transitions.standard,
    },
    toolbar: {
      minHeight: '64px',
    },
  },

  // Menu styles
  menu: {
    paper: {
      backgroundColor: tokens.colors.alpha.black[900],
      backdropFilter: 'blur(8px)',
      borderRadius: tokens.radius.base,
      boxShadow: tokens.shadows.elegant,
      border: `1px solid ${tokens.colors.borders.light}`,
    },
    list: {
      padding: `${tokens.spacing[1]} 0`,
    },
    item: {
      fontSize: tokens.typography.fontSizes.sm,
      fontWeight: tokens.typography.fontWeights.normal,
      padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
      transition: tokens.transitions.standard,
      '&:hover': {
        backgroundColor: tokens.colors.alpha.black[50],
      },
    },
  },

  // --- NEW: Tooltip Styles ---
  tooltip: {
    tooltip: { // Styles for the tooltip itself
      backgroundColor: tokens.colors.neutral[800], // Darker background for contrast
      color: tokens.colors.neutral[100], // Lighter text color
      fontSize: tokens.typography.fontSizes.xs,
      padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
      borderRadius: tokens.radius.sm,
      boxShadow: tokens.shadows.md,
    },
    arrow: { // Styles for the arrow
      color: tokens.colors.neutral[800], // Match arrow color to tooltip background
    },
  },
  // --- END NEW ---
};

export default components; 